import React, { useEffect, useState } from "react";
import {
  getSalaryEmployeeDetailV2,
  getSalaryEmployeeHistoryV2,
  patchSalaryEmployeeHistoryV2,
  patchSubstitutionPaymentPercentV2,
} from "../../../../api/timesheetApi";

const FIELD_TO_KIND = {
  annualLeave: "vacation",
  sickLeave: "sick_leave",
  substitution: "substitution",
};

const KIND_META = {
  vacation: {
    title: "Расчёт отпускных",
    formulaLines: ["СрД = ΣЗП12 / ΣД12", "Отпускные = СрД × Д_отп"],
    daysLabel: "Дни отпуска",
  },
  sick_leave: {
    title: "Расчёт больничных",
    formulaLines: ["СрД = ΣЗП12 / ΣД12", "Больничные = СрД × Д_боль"],
    daysLabel: "Дни больничного",
  },
  substitution: {
    title: "Расчёт замещения",
    formulaLines: [
      "Замещение = (ЗП отсутствующего / Р.дн.) × Дн. замещения × % замещения",
    ],
    daysLabel: null,
  },
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(
    Number(n) || 0,
  );

const keepDigitsAndDot = (value) =>
  String(value ?? "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");

const formatPeriodRange = (periodStr) => {
  if (!periodStr || typeof periodStr !== "string") return "—";
  const trimmed = periodStr.trim();
  if (!trimmed) return "—";
  return trimmed.replace(/\s*-\s*/g, "–");
};

const getPreviousReferencePeriod = (year, month) => {
  const numericYear = Number(year);
  const numericMonth = Number(month);
  if (!Number.isInteger(numericYear) || !Number.isInteger(numericMonth)) {
    return { reference_year: year, reference_month: month };
  }
  if (numericMonth <= 1) {
    return { reference_year: numericYear - 1, reference_month: 12 };
  }
  return { reference_year: numericYear, reference_month: numericMonth - 1 };
};

const periodToIndex = (year, month) => Number(year) * 12 + Number(month);

const shiftPeriod = (year, month, diffMonths) => {
  const date = new Date(Number(year), Number(month) - 1 + Number(diffMonths), 1);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
  };
};

const isPeriodInRange = (period, startPeriod, endPeriod) => {
  if (!period || !startPeriod || !endPeriod) return false;
  const current = periodToIndex(period.year, period.month);
  const start = periodToIndex(startPeriod.year, startPeriod.month);
  const end = periodToIndex(endPeriod.year, endPeriod.month);
  return current >= start && current <= end;
};

const MRP_BY_YEAR = {
  2025: 3932,
  2026: 4325,
};

const RU_MONTHS_TO_NUMBER = {
  январь: 1,
  января: 1,
  февраль: 2,
  февраля: 2,
  март: 3,
  марта: 3,
  апрель: 4,
  апреля: 4,
  май: 5,
  мая: 5,
  июнь: 6,
  июня: 6,
  июль: 7,
  июля: 7,
  август: 8,
  августа: 8,
  сентябрь: 9,
  сентября: 9,
  октябрь: 10,
  октября: 10,
  ноябрь: 11,
  ноября: 11,
  декабрь: 12,
  декабря: 12,
};

/** Первое из значений, которое можно интерпретировать как число (включая 0). */
const coalesceNumeric = (...rawValues) => {
  for (const raw of rawValues) {
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const pickSickLeavePeriod = (detail) => {
  if (!detail || typeof detail !== "object") return null;
  const p = detail.sick_leave_period ?? detail.sickLeavePeriod;
  return typeof p === "string" ? p : p != null ? String(p) : null;
};

/** Лимит выплаты по больничному (25 МРП), тг — из API, МРП в ответе или справочника по году. */
const pickSickLeave25MrpCapTenge = (detail, year) => {
  if (!detail || typeof detail !== "object") return null;
  const explicit = coalesceNumeric(
    detail.sick_leave_25_mrp_limit,
    detail.sick_leave_25_mrp_amount,
    detail.sickLeave25MrpLimit,
    detail.sick_leave_mrp_limit_amount,
    detail.limit_25_mrp,
    detail.limit_25_mrp_amount,
    detail.mrp_limit_25,
    detail.max_sick_leave_by_mrp,
    detail.sick_benefit_cap_25_mrp,
  );
  if (explicit !== null) return explicit;
  const mrpUnit = coalesceNumeric(
    detail.mrp,
    detail.MRP,
    detail.mrp_value,
    detail.monthly_calculation_index,
    detail.monthly_calculation_index_value,
  );
  if (mrpUnit !== null) return mrpUnit * 25;
  const mrpByYear = coalesceNumeric(MRP_BY_YEAR[Number(year)]);
  if (mrpByYear !== null) return mrpByYear * 25;
  return null;
};

const resolveHistoryPeriod = (row) => {
  const directYear = Number(row?.year);
  const directMonth = Number(row?.month);
  if (
    Number.isInteger(directYear) &&
    Number.isInteger(directMonth) &&
    directMonth >= 1 &&
    directMonth <= 12
  ) {
    return { year: directYear, month: directMonth };
  }

  const label = String(row?.month_label ?? "")
    .trim()
    .toLowerCase();
  if (!label) return null;

  const dotMatch = label.match(/(\d{1,2})\.(\d{4})/);
  if (dotMatch) {
    const month = Number(dotMatch[1]);
    const year = Number(dotMatch[2]);
    if (month >= 1 && month <= 12) return { year, month };
  }

  const yearMatch = label.match(/(20\d{2})/);
  const year = yearMatch ? Number(yearMatch[1]) : null;
  const monthName = Object.keys(RU_MONTHS_TO_NUMBER).find((m) =>
    label.includes(m),
  );
  if (year && monthName) {
    return { year, month: RU_MONTHS_TO_NUMBER[monthName] };
  }

  return null;
};

function SalaryCalculationDrawer({
  open,
  onClose,
  context,
  year,
  month,
  periodLabel,
}) {
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [show12Months, setShow12Months] = useState(false);
  const [editedHistoryAmounts, setEditedHistoryAmounts] = useState({});
  const [substitutionMode, setSubstitutionMode] = useState("percent");
  const [substitutionPercentInput, setSubstitutionPercentInput] = useState("");
  const [substitutionFixedInput, setSubstitutionFixedInput] = useState("");
  const [substitutionSaving, setSubstitutionSaving] = useState(false);
  const [substitutionSaveError, setSubstitutionSaveError] = useState(null);
  const [substitutionSaveSuccess, setSubstitutionSaveSuccess] = useState(null);
  const [historySaving, setHistorySaving] = useState(false);
  const [historySaveError, setHistorySaveError] = useState(null);
  const [historySaveSuccess, setHistorySaveSuccess] = useState(null);

  const kind =
    context?.fieldName != null
      ? FIELD_TO_KIND[context.fieldName] || null
      : null;
  const meta = kind ? KIND_META[kind] : null;

  useEffect(() => {
    if (!open || !context?.userId || !kind) {
      setDetail(null);
      setHistory(null);
      setError(null);
      setShow12Months(false);
      setEditedHistoryAmounts({});
      setSubstitutionMode("percent");
      setSubstitutionPercentInput("");
      setSubstitutionFixedInput("");
      setSubstitutionSaveError(null);
      setSubstitutionSaveSuccess(null);
      setHistorySaving(false);
      setHistorySaveError(null);
      setHistorySaveSuccess(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setDetail(null);
      setHistory(null);
      setEditedHistoryAmounts({});
      setSubstitutionSaveError(null);
      setSubstitutionSaveSuccess(null);
      setHistorySaving(false);
      setHistorySaveError(null);
      setHistorySaveSuccess(null);

      try {
        const detailPromise = getSalaryEmployeeDetailV2(context.userId, {
          year,
          month,
        });
        const historyPromise =
          kind === "vacation" || kind === "sick_leave"
            ? getSalaryEmployeeHistoryV2(context.userId, {
                reference_year: year,
                reference_month: month,
              })
            : Promise.resolve(null);

        const [detailRes, historyRes] = await Promise.all([
          detailPromise,
          historyPromise,
        ]);

        if (!cancelled) {
          setDetail(detailRes);
          setHistory(historyRes);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Не удалось загрузить данные");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, context?.userId, context?.fieldName, kind, year, month]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || kind !== "substitution" || !detail) return;
    const initialPercent = coalesceNumeric(
      detail.substitution_payment_percent,
      0,
    );
    const initialFixed = coalesceNumeric(detail.substitution_amount, 0);
    setSubstitutionPercentInput(
      String(Math.max(0, Number(initialPercent) || 0)),
    );
    setSubstitutionFixedInput(String(Math.max(0, Number(initialFixed) || 0)));
    setSubstitutionMode("percent");
    setSubstitutionSaveError(null);
    setSubstitutionSaveSuccess(null);
  }, [open, kind, detail]);

  if (!open || !context) return null;

  const rowTotals = context.rowTotals || {};
  const isPaidOut =
    Number(rowTotals.remaining) === 0 && Number(rowTotals.salary) > 0;

  const payoutAmount =
    kind === "vacation"
      ? detail?.vacation_amount
      : kind === "sick_leave"
        ? detail?.sick_leave_amount
        : kind === "substitution"
          ? detail?.substitution_amount
          : null;

  const substitutionSource = {
    workdays: coalesceNumeric(detail?.workdays, 0) || 0,
    substitutionDays: coalesceNumeric(detail?.substitution_days, 0) || 0,
    percentFromApi:
      coalesceNumeric(detail?.substitution_payment_percent, 0) || 0,
    amountFromApi: coalesceNumeric(detail?.substitution_amount, 0) || 0,
    absentSalaryFromApi:
      coalesceNumeric(detail?.absent_user_salary, detail?.absentSalary) || null,
  };
  const substitutionPercentValue = Math.max(
    0,
    Number(keepDigitsAndDot(substitutionPercentInput)) || 0,
  );
  const substitutionFixedValue = Math.max(
    0,
    Number(keepDigitsAndDot(substitutionFixedInput)) || 0,
  );
  const substitutionPaymentId = coalesceNumeric(
    detail?.substitution_payment_id,
    detail?.substitutionPaymentId,
    detail?.payment_id,
    detail?.substitution_payment?.id,
  );
  const substitutionAbsentSalary =
    substitutionSource.absentSalaryFromApi !== null
      ? substitutionSource.absentSalaryFromApi
      : substitutionSource.workdays > 0 &&
          substitutionSource.substitutionDays > 0 &&
          substitutionSource.percentFromApi > 0
        ? (substitutionSource.amountFromApi * substitutionSource.workdays) /
          (substitutionSource.substitutionDays *
            (substitutionSource.percentFromApi / 100))
        : 0;
  const substitutionAvgDay =
    substitutionSource.workdays > 0
      ? substitutionAbsentSalary / substitutionSource.workdays
      : 0;
  const substitutionPercentAmount =
    substitutionSource.workdays > 0
      ? substitutionAvgDay *
        substitutionSource.substitutionDays *
        (substitutionPercentValue / 100)
      : 0;
  const substitutionDisplayAmount =
    substitutionMode === "fixed"
      ? substitutionFixedValue
      : substitutionPercentAmount;

  const vacationCalendar = detail ? Number(detail.vacation_calendar_days) : 0;
  const vacationWorkingFromTimesheet = Number(
    context.timesheet?.annualLeave ?? 0,
  );
  const sickDaysResolved = coalesceNumeric(
    detail?.sick_leave_days,
    detail?.sickLeaveDays,
    context?.sickLeaveDaysFromPreview,
  );
  const sickLeave25MrpCapExplicit = pickSickLeave25MrpCapTenge(detail, year);
  const serverSickLeaveAmount =
    kind === "sick_leave" ? coalesceNumeric(detail?.sick_leave_amount) : null;
  const historyRowsRaw = Array.isArray(history?.history) ? history.history : [];
  const historyReferencePeriod = getPreviousReferencePeriod(year, month);
  const historyWindowEnd = {
    year: historyReferencePeriod.reference_year,
    month: historyReferencePeriod.reference_month,
  };
  const historyWindowStart = shiftPeriod(
    historyWindowEnd.year,
    historyWindowEnd.month,
    -11,
  );
  const historyRows = historyRowsRaw.filter((row) => {
    const period = resolveHistoryPeriod(row);
    return isPeriodInRange(period, historyWindowStart, historyWindowEnd);
  });
  const effectiveHistoryRows = historyRows.map((row, index) => {
    const key = `${row.month_label}-${index}`;
    const editedAmount = editedHistoryAmounts[key];
    return {
      ...row,
      amount:
        editedAmount !== undefined ? editedAmount : Number(row.amount) || 0,
      workdays: Number(row.workdays) || 0,
    };
  });
  const effectiveTotalAmount12Months = effectiveHistoryRows.reduce(
    (sum, row) => sum + (Number(row.amount) || 0),
    0,
  );
  const effectiveWorkedDays12Months = effectiveHistoryRows.reduce(
    (sum, row) => sum + (Number(row.workdays) || 0),
    0,
  );
  const effectiveAveragePerDay =
    effectiveWorkedDays12Months > 0
      ? effectiveTotalAmount12Months / effectiveWorkedDays12Months
      : 0;
  const daysForPayout =
    kind === "vacation"
      ? coalesceNumeric(vacationWorkingFromTimesheet, vacationCalendar)
      : kind === "sick_leave"
        ? sickDaysResolved
        : null;
  const uncappedFormulaPayout =
    daysForPayout !== null
      ? effectiveAveragePerDay * Number(daysForPayout || 0)
      : null;
  const hasHistoryChanges = effectiveHistoryRows.some((row, index) => {
    const original = Number(historyRows[index]?.amount) || 0;
    const current = Number(row.amount) || 0;
    return original !== current;
  });
  const sickLeave25MrpCapResolved = sickLeave25MrpCapExplicit;
  const effectivePayoutAmount = (() => {
    if (kind === "sick_leave") {
      if (!hasHistoryChanges && serverSickLeaveAmount !== null) {
        return serverSickLeaveAmount;
      }
      if (
        uncappedFormulaPayout !== null &&
        sickLeave25MrpCapResolved !== null
      ) {
        return Math.min(uncappedFormulaPayout, sickLeave25MrpCapResolved);
      }
      if (uncappedFormulaPayout !== null) return uncappedFormulaPayout;
      return serverSickLeaveAmount ?? payoutAmount;
    }
    if (uncappedFormulaPayout !== null) return uncappedFormulaPayout;
    return payoutAmount;
  })();

  const handleHistoryAmountChange = (key, value) => {
    const cleaned = String(value).replace(/[^\d]/g, "");
    if (cleaned === "") {
      setEditedHistoryAmounts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed)) return;
    setEditedHistoryAmounts((prev) => ({
      ...prev,
      [key]: parsed,
    }));
  };

  const handleSaveHistoryChanges = async () => {
    if (!history || !Array.isArray(history.history) || !hasHistoryChanges) {
      return;
    }

    const rowsToSync = historyRows
      .map((row, index) => {
        const period = resolveHistoryPeriod(row);
        if (!period) {
          return {
            invalid: true,
            label: row?.month_label || "неизвестный месяц",
          };
        }

        const key = `${row.month_label}-${index}`;
        const editedAmount = editedHistoryAmounts[key];
        const amount =
          editedAmount !== undefined ? editedAmount : Number(row.amount) || 0;

        return {
          year: period.year,
          month: period.month,
          amount,
        };
      })
      .sort((a, b) => {
        if (a.invalid || b.invalid) return 0;
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

    const invalidRows = rowsToSync.filter((item) => item.invalid);
    if (invalidRows.length > 0) {
      const invalidLabel = invalidRows[0]?.label || "неизвестный месяц";
      setHistorySaveError(
        `Не удалось определить период для строки "${invalidLabel}".`,
      );
      return;
    }

    setHistorySaving(true);
    setHistorySaveError(null);
    setHistorySaveSuccess(null);

    try {
      let latestResponse = null;
      for (const row of rowsToSync) {
        if (row.invalid) continue;
        latestResponse = await patchSalaryEmployeeHistoryV2(context.userId, {
          year: row.year,
          month: row.month,
          amount: row.amount,
          reason: "Синхронизация истории зарплаты за 12 месяцев",
        });
      }

      if (latestResponse?.history) {
        setHistory(latestResponse.history);
      } else {
        const updatedHistory = historyRows.map((row, index) => {
          const key = `${row.month_label}-${index}`;
          const editedAmount = editedHistoryAmounts[key];
          if (editedAmount === undefined) return row;
          return {
            ...row,
            amount: editedAmount,
          };
        });

        setHistory((prev) =>
          prev
            ? {
                ...prev,
                history: updatedHistory,
                total_amount_12_months: updatedHistory.reduce(
                  (sum, row) => sum + (Number(row.amount) || 0),
                  0,
                ),
                worked_days_12_months: updatedHistory.reduce(
                  (sum, row) => sum + (Number(row.workdays) || 0),
                  0,
                ),
              }
            : prev,
        );
      }

      setEditedHistoryAmounts({});
      setHistorySaveSuccess("История зарплаты сохранена");
    } catch (e) {
      setHistorySaveError(
        e?.message || "Не удалось сохранить историю зарплаты",
      );
    } finally {
      setHistorySaving(false);
    }
  };

  const getHistorySaveButtonLabel = () => {
    if (historySaving) return "Сохранение...";
    return "Сохранить";
  };

  const hasHistoryData = historyRows.length > 0;
  const canSaveHistoryChanges =
    hasHistoryChanges && !historySaving && !!context?.userId && hasHistoryData;

  const getHistoryActionVisible = () =>
    show12Months &&
    (hasHistoryChanges || historySaveError || historySaveSuccess);

  const getHistoryInputDisabled = () => historySaving;

  const handleSubstitutionSavePercent = async () => {
    if (kind !== "substitution" || substitutionPaymentId === null) {
      setSubstitutionSaveError("Не найден payment_id для сохранения процента.");
      return;
    }
    setSubstitutionSaving(true);
    setSubstitutionSaveError(null);
    setSubstitutionSaveSuccess(null);
    try {
      await patchSubstitutionPaymentPercentV2(substitutionPaymentId, {
        payment_percent: substitutionPercentValue,
        reason: "Изменение процента в расчёте замещения",
      });

      const recalculatedAmount =
        substitutionSource.workdays > 0
          ? substitutionAvgDay *
            substitutionSource.substitutionDays *
            (substitutionPercentValue / 100)
          : substitutionSource.amountFromApi;

      setDetail((prev) =>
        prev
          ? {
              ...prev,
              substitution_payment_percent: substitutionPercentValue,
              substitution_amount: recalculatedAmount,
            }
          : prev,
      );
      setSubstitutionFixedInput(String(Math.round(recalculatedAmount)));
      setSubstitutionSaveSuccess("Процент замещения сохранён");
    } catch (e) {
      setSubstitutionSaveError(
        e?.message || "Не удалось сохранить процент замещения",
      );
    } finally {
      setSubstitutionSaving(false);
    }
  };

  const renderUnsupported = () => (
    <p className="salary-calc-drawer__muted">
      Для этого показателя детальный расчёт по выбранному полю пока не
      подключён. Используйте кнопку «Расчёты» у отпуска, больничного или
      замещения.
    </p>
  );

  const renderBody = () => {
    if (!kind) return renderUnsupported();
    if (loading) {
      return <p className="salary-calc-drawer__muted">Загрузка данных…</p>;
    }
    if (error) {
      return <p className="salary-calc-drawer__error">{error}</p>;
    }
    if (!detail) return null;

    if (kind === "vacation" || kind === "sick_leave") {
      const periodStr =
        kind === "vacation"
          ? (detail.vacation_period ?? detail.vacationPeriod)
          : pickSickLeavePeriod(detail);
      const daysStrong =
        kind === "vacation" ? (
          <strong>
            {vacationCalendar
              ? `${formatCurrency(vacationCalendar)} к.дн.`
              : "—"}
            {vacationWorkingFromTimesheet
              ? ` · ${formatCurrency(vacationWorkingFromTimesheet)} р.дн.`
              : ""}
          </strong>
        ) : (
          <strong>
            {sickDaysResolved !== null
              ? `${formatCurrency(sickDaysResolved)} дн.`
              : "—"}
          </strong>
        );

      return (
        <>
          <button
            type="button"
            className="salary-calc-drawer__toggle"
            onClick={() => setShow12Months((v) => !v)}
          >
            {show12Months ? "Скрыть 12 мес" : "Показать 12 мес"}
            <span className="salary-calc-drawer__chevron">
              {show12Months ? "▲" : "▼"}
            </span>
          </button>

          <div className="salary-calc-drawer__card salary-calc-drawer__card--muted">
            <div className="salary-calc-drawer__row">
              <span>Начислено за 12 мес</span>
              <strong>
                {history ? formatCurrency(effectiveTotalAmount12Months) : "—"}
              </strong>
            </div>
            <div className="salary-calc-drawer__row">
              <span>Отработано дней за 12 мес</span>
              <strong>
                {history ? formatCurrency(effectiveWorkedDays12Months) : "—"}
              </strong>
            </div>
            <div className="salary-calc-drawer__row">
              <span>{meta.daysLabel}</span>
              {daysStrong}
            </div>
            <div className="salary-calc-drawer__row">
              <span>Период</span>
              <strong>{formatPeriodRange(periodStr)}</strong>
            </div>
            {kind === "sick_leave" && sickLeave25MrpCapResolved !== null ? (
              <div
                className="salary-calc-drawer__mrp-limit"
                role="status"
                aria-label="Лимит выплаты по больничному 25 МРП"
              >
                <span
                  className="salary-calc-drawer__mrp-limit-icon"
                  aria-hidden
                >
                  ⚠
                </span>
                <span className="salary-calc-drawer__mrp-limit-label">
                  Лимит 25 МРП
                </span>
                <strong className="salary-calc-drawer__mrp-limit-value">
                  {formatCurrency(sickLeave25MrpCapResolved)}
                </strong>
              </div>
            ) : null}
          </div>

          {show12Months && historyRows.length > 0 && (
            <div className="salary-calc-drawer__history">
              <div className="salary-calc-drawer__history-head">
                <span>Месяц</span>
                <span>Сумма</span>
                <span>Раб. дни</span>
              </div>
              {effectiveHistoryRows.map((row, i) => {
                const rowKey = `${row.month_label}-${i}`;
                const amountDisplay = formatCurrency(row.amount);
                const inputWidthCh = Math.max(
                  7,
                  Math.min(14, amountDisplay.length + 1),
                );
                return (
                  <div key={rowKey} className="salary-calc-drawer__history-row">
                    <span>{row.month_label}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="salary-calc-drawer__history-input"
                      value={amountDisplay}
                      style={{ "--salary-history-input-ch": inputWidthCh }}
                      onChange={(e) =>
                        handleHistoryAmountChange(rowKey, e.target.value)
                      }
                      disabled={getHistoryInputDisabled()}
                      aria-label={`Сумма за ${row.month_label}`}
                    />
                    <span>{formatCurrency(row.workdays)}</span>
                  </div>
                );
              })}
            </div>
          )}
          {getHistoryActionVisible() && (
            <div className="salary-calc-drawer__history-actions">
              <button
                type="button"
                className="salary-calc-drawer__save-button"
                onClick={handleSaveHistoryChanges}
                disabled={!canSaveHistoryChanges}
              >
                {getHistorySaveButtonLabel()}
              </button>
              {historySaveError ? (
                <p className="salary-calc-drawer__error">{historySaveError}</p>
              ) : null}
              {historySaveSuccess ? (
                <p className="salary-calc-drawer__sub-success">
                  {historySaveSuccess}
                </p>
              ) : null}
            </div>
          )}

          <div className="salary-calc-drawer__section-title">Расчёт</div>
          <div className="salary-calc-drawer__formula">
            {meta.formulaLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>

          <div className="salary-calc-drawer__totals">
            <div className="salary-calc-drawer__row">
              <span>Ср. ЗП в день</span>
              <strong>
                {history ? formatCurrency(effectiveAveragePerDay) : "—"}
              </strong>
            </div>
            {kind === "sick_leave" &&
            uncappedFormulaPayout !== null &&
            sickLeave25MrpCapResolved !== null &&
            uncappedFormulaPayout > effectivePayoutAmount + 0.5 ? (
              <div className="salary-calc-drawer__row salary-calc-drawer__row--ref">
                <span>Справочно: СрД × дни (без лимита)</span>
                <strong>{formatCurrency(uncappedFormulaPayout)} тг</strong>
              </div>
            ) : null}
            <div className="salary-calc-drawer__row salary-calc-drawer__row--highlight">
              <span>Итого к выплате</span>
              <strong>{formatCurrency(effectivePayoutAmount)} тг</strong>
            </div>
          </div>
        </>
      );
    }

    if (kind === "substitution") {
      const canSavePercent =
        substitutionMode === "percent" &&
        substitutionPaymentId !== null &&
        !substitutionSaving;

      return (
        <>
          {detail.substitution_period ? (
            <div className="salary-calc-drawer__subst-bar">
              Замещение: {formatPeriodRange(detail.substitution_period)}
            </div>
          ) : null}

          <div className="salary-calc-drawer__sub-mode-title">
            Способ расчёта замещения
          </div>
          <div className="salary-calc-drawer__sub-mode-group">
            <label className="salary-calc-drawer__sub-mode-item">
              <input
                type="radio"
                name="substitution-mode"
                checked={substitutionMode === "percent"}
                onChange={() => setSubstitutionMode("percent")}
              />
              <span>По проценту от оклада</span>
            </label>
            <label className="salary-calc-drawer__sub-mode-item">
              <input
                type="radio"
                name="substitution-mode"
                checked={substitutionMode === "fixed"}
                onChange={() => setSubstitutionMode("fixed")}
              />
              <span>Фиксированная сумма</span>
            </label>
          </div>

          <div className="salary-calc-drawer__card">
            {substitutionMode === "percent" ? (
              <>
                <div className="salary-calc-drawer__row">
                  <span>ЗП отсутствующего</span>
                  <strong>{formatCurrency(substitutionAbsentSalary)}</strong>
                </div>
                <div className="salary-calc-drawer__row">
                  <span>Рабочих дней</span>
                  <strong>{formatCurrency(substitutionSource.workdays)}</strong>
                </div>
                <div className="salary-calc-drawer__row">
                  <span>Дни замещения</span>
                  <strong>
                    {formatCurrency(substitutionSource.substitutionDays)}
                  </strong>
                </div>
                <div className="salary-calc-drawer__sub-input-row">
                  <label htmlFor="substitution-percent-input">
                    % замещения
                  </label>
                  <input
                    id="substitution-percent-input"
                    className="salary-calc-drawer__sub-input"
                    type="text"
                    inputMode="decimal"
                    value={substitutionPercentInput}
                    onChange={(e) =>
                      setSubstitutionPercentInput(
                        keepDigitsAndDot(e.target.value),
                      )
                    }
                    disabled={substitutionSaving}
                  />
                </div>
                <div className="salary-calc-drawer__sub-actions">
                  <button
                    type="button"
                    className="salary-calc-drawer__save-button"
                    onClick={handleSubstitutionSavePercent}
                    disabled={!canSavePercent}
                  >
                    {substitutionSaving ? "Сохранение..." : "Сохранить процент"}
                  </button>
                </div>
              </>
            ) : (
              <div className="salary-calc-drawer__sub-input-row">
                <label htmlFor="substitution-fixed-input">
                  Фиксированная сумма доплаты, тг
                </label>
                <input
                  id="substitution-fixed-input"
                  className="salary-calc-drawer__sub-input"
                  type="text"
                  inputMode="numeric"
                  value={substitutionFixedInput}
                  onChange={(e) =>
                    setSubstitutionFixedInput(
                      keepDigitsAndDot(e.target.value).replace(/\./g, ""),
                    )
                  }
                />
              </div>
            )}
            {substitutionSaveError ? (
              <p className="salary-calc-drawer__error">
                {substitutionSaveError}
              </p>
            ) : null}
            {substitutionSaveSuccess ? (
              <p className="salary-calc-drawer__sub-success">
                {substitutionSaveSuccess}
              </p>
            ) : null}
          </div>

          {substitutionMode === "percent" ? (
            <>
              <div className="salary-calc-drawer__section-title">Расчёт</div>
              <div className="salary-calc-drawer__formula">
                {meta.formulaLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </>
          ) : null}

          <div className="salary-calc-drawer__totals">
            {substitutionMode === "percent" ? (
              <div className="salary-calc-drawer__row">
                <span>Средний дневной ЗП</span>
                <strong>{formatCurrency(substitutionAvgDay)}</strong>
              </div>
            ) : null}
            <div className="salary-calc-drawer__row salary-calc-drawer__row--highlight">
              <span>Итого к выплате</span>
              <strong>{formatCurrency(substitutionDisplayAmount)} тг</strong>
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  const title = meta?.title || "Расчёт";
  const badgeClass = isPaidOut
    ? "salary-calc-drawer__badge salary-calc-drawer__badge--paid"
    : "salary-calc-drawer__badge salary-calc-drawer__badge--pending";
  const badgeAmount =
    (kind === "vacation" || kind === "sick_leave"
      ? effectivePayoutAmount
      : kind === "substitution"
        ? substitutionDisplayAmount
        : payoutAmount) ??
    context.amount ??
    rowTotals.paid ??
    0;
  const badgeText = isPaidOut
    ? `Выплачено: ${formatCurrency(badgeAmount)} тг`
    : "Не выплачено";

  return (
    <div className="salary-calc-drawer">
      <button
        type="button"
        className="salary-calc-drawer__backdrop"
        aria-label="Закрыть"
        onClick={onClose}
      />
      <aside
        className="salary-calc-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="salary-calc-drawer-title"
      >
        <div className="salary-calc-drawer__head">
          <h2
            id="salary-calc-drawer-title"
            className="salary-calc-drawer__title"
          >
            {title}
          </h2>
          <div className="salary-calc-drawer__head-right">
            {kind ? <span className={badgeClass}>{badgeText}</span> : null}
            <button
              type="button"
              className="salary-calc-drawer__close"
              onClick={onClose}
              aria-label="Закрыть панель"
            >
              ×
            </button>
          </div>
        </div>

        <div className="salary-calc-drawer__meta">
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">Отделение</span>
            <span>{context.departmentName || "—"}</span>
          </div>
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">Отдел</span>
            <span>{context.groupName || "—"}</span>
          </div>
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">Период</span>
            <span>{periodLabel}</span>
          </div>
          <div className="salary-calc-drawer__meta-row">
            <span className="salary-calc-drawer__meta-label">ФИО</span>
            <span>{context.employeeName || "—"}</span>
          </div>
        </div>

        <div className="salary-calc-drawer__body">{renderBody()}</div>
      </aside>
    </div>
  );
}

export default SalaryCalculationDrawer;
